// PlazaStack (pag-commerce) public storefront API shapes. Mirrors
// pag-commerce/docs/API-CONTRACT.md plus the pre-order / restock additions on
// variants. The contract allows additive fields, so everything here is
// structural: readers must tolerate unknown and missing fields.

export interface PsImage {
  id:       string;
  url:      string;           // absolute, immutable, opaque
  alt:      string | null;
  position: number;
  width:    number | null;
  height:   number | null;
}

export interface PsPreorder {
  available: boolean;
  /** ISO date the pre-order is expected to ship by, when known. */
  ships_by:  string | null;
  /** Units still available to pre-order, null = unlimited. */
  remaining: number | null;
}

export interface PsVariant {
  id:          string;
  sku:         string | null;
  title:       string;
  size:        string | null;
  position?:   number;
  /** null inherits the product price. */
  price_cents: number | null;
  inventory:   { available: number; in_stock: boolean };
  preorder?:   PsPreorder | null;
  restock_alert?: { available: boolean } | null;
}

export interface PsProduct {
  id:                     string;
  slug:                   string;
  name:                   string;
  description:            string | null;
  brand:                  string | null;
  status?:                string;
  price_cents:            number;
  compare_at_price_cents: number | null;
  currency?:              string;
  collections:            string[];
  images:                 PsImage[];
  variants:               PsVariant[];
  created_at:             string;
  updated_at:             string;
}

export interface PsCollection {
  id:            string;
  slug:          string;
  name:          string;
  description:   string | null;
  position:      number;
  product_count: number;
}

export interface PsPage<T> {
  data: T[];
  page: { next_cursor: string | null; has_more: boolean; limit: number };
}

export interface PsCheckoutInput {
  items:           { variant_id: string; quantity: number }[];
  success_url:     string;
  cancel_url:      string;
  customer_email?: string;
}

export interface PsCheckoutCreated {
  checkout_url: string;
  session_id:   string;
  order_id:     string;
  expires_at:   string;
}

export interface PsCheckoutSession {
  status:        'pending' | 'paid' | 'expired' | (string & {});
  order_id?:     string;
  order_number?: number;
  email?:        string;
  total_cents?:  number;
  currency?:     string;
}

export class PsError extends Error {
  readonly status:  number;
  readonly code:    string;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'PsError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}
